import { CircleX, TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { fillPlaceholders } from '@/services/core/apiError'
import type { ValidationWarning } from '@/services/core/validationWarnings'

/** The API's warning text with its `{{key}}` placeholders filled in. */
export function warningText(w: ValidationWarning): string {
  return fillPlaceholders(w.text, w.parameters)
}

function WarningGroup({
  warnings,
  title,
  variant,
}: {
  warnings: ValidationWarning[]
  title: string
  variant: 'destructive' | 'warning'
}) {
  if (!warnings.length) return null
  const Icon = variant === 'destructive' ? CircleX : TriangleAlert
  return (
    <Alert variant={variant}>
      <Icon />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        <ul className="list-disc space-y-0.5 pl-4">
          {warnings.map((w, i) => (
            <li key={`${w.code}-${i}`}>{warningText(w)}</li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  )
}

/**
 * The server's validation warnings for an object, grouped into one alert for
 * those that stop it from serving and one for the rest. Goes at the top of an
 * edit page, above its header; map the API field with `mapValidationWarnings`.
 */
export function ValidationWarnings({ warnings }: { warnings?: ValidationWarning[] }) {
  const { t } = useTranslation()
  if (!warnings?.length) return null
  return (
    <>
      <WarningGroup
        variant="destructive"
        title={t('common.warningsBlocking')}
        warnings={warnings.filter((w) => w.preventsAdServing)}
      />
      <WarningGroup
        variant="warning"
        title={t('common.warningsOther')}
        warnings={warnings.filter((w) => !w.preventsAdServing)}
      />
    </>
  )
}

/**
 * A warning icon that lists an object's validation warnings on hover or focus,
 * for a row in a list. Red when one of them stops the object from serving.
 */
export function ValidationWarningsIcon({ warnings }: { warnings: ValidationWarning[] }) {
  const { t } = useTranslation()
  if (!warnings.length) return null
  const blocking = warnings.filter((w) => w.preventsAdServing)
  const other = warnings.filter((w) => !w.preventsAdServing)
  const groups = [
    { title: t('common.warningsBlocking'), warnings: blocking },
    { title: t('common.warningsOther'), warnings: other },
  ].filter((g) => g.warnings.length)
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          tabIndex={0}
          aria-label={blocking.length ? t('common.warningsBlocking') : t('common.warningsOther')}
          className={cn(
            'inline-flex shrink-0 rounded-sm',
            blocking.length ? 'text-(--ad-danger)' : 'text-(--ad-warning)',
          )}
        >
          <TriangleAlert className="size-4" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-xs text-left">
        <div className="space-y-1.5">
          {groups.map((g) => (
            <div key={g.title}>
              <p className="font-semibold">{g.title}</p>
              <ul className="list-disc space-y-0.5 pl-4">
                {g.warnings.map((w, i) => (
                  <li key={`${w.code}-${i}`}>{warningText(w)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
